import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getRuntimeStatus } from "../api/ops.ts";
import { useAuth } from "../context/auth.tsx";
import { Layout } from "../components/layout.tsx";

interface RuntimeStatus {
  activeAgents: number;
  totalAgents: number;
  runningJobs: number;
  queuedJobs: number;
  scheduledJobs: number;
  totalJobsToday: number;
  successRateToday: number;
  totalCostToday: number;
  totalTokensToday: number;
}

export function OpsOverviewPage() {
  const { token } = useAuth();
  const [status, setStatus] = useState<RuntimeStatus | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void getRuntimeStatus(token).then((d) => {
      setStatus(d as RuntimeStatus);
      setLoading(false);
    });
  }, []);

  return (
    <Layout>
      <div className="mx-auto max-w-6xl p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white">AI Operations Platform</h1>
          <p className="mt-1 text-gray-400">Phase 7 — Autonomous Agent Runtime & Monitoring</p>
        </div>

        {loading ? (
          <p className="text-gray-400">Loading runtime status...</p>
        ) : status ? (
          <div className="mb-8 grid grid-cols-2 gap-4 md:grid-cols-3">
            {[
              { label: "Active Agents", value: `${status.activeAgents}/${status.totalAgents}` },
              { label: "Running Jobs", value: status.runningJobs },
              { label: "Queued Jobs", value: status.queuedJobs },
              { label: "Jobs Today", value: status.totalJobsToday },
              { label: "Success Rate", value: `${(status.successRateToday * 100).toFixed(1)}%` },
              { label: "Cost Today", value: `$${status.totalCostToday.toFixed(4)}` },
            ].map((s) => (
              <div key={s.label} className="rounded-lg border border-gray-800 bg-gray-900 p-4">
                <p className="text-xs text-gray-400">{s.label}</p>
                <p className="mt-1 text-2xl font-bold text-white">{s.value}</p>
              </div>
            ))}
          </div>
        ) : null}

        <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
          {[
            { to: "/ops/agents", label: "Agent Registry", desc: "Registered agents & health" },
            { to: "/ops/jobs", label: "Running Jobs", desc: "Active job execution monitor" },
            { to: "/ops/scheduler", label: "Scheduler", desc: "Cron & recurring jobs" },
            { to: "/ops/queue", label: "Queue Monitor", desc: "Job queue & priorities" },
            { to: "/ops/tools", label: "Tool Registry", desc: "Available tools & usage" },
            { to: "/ops/planner", label: "AI Planner", desc: "Execution plans & steps" },
            { to: "/ops/costs", label: "Cost Dashboard", desc: "Token usage & AI costs" },
            { to: "/ops/metrics", label: "Metrics", desc: "Runtime performance metrics" },
            { to: "/ops/health", label: "Agent Health", desc: "Health status & heartbeats" },
          ].map((nav) => (
            <Link
              key={nav.to}
              to={nav.to}
              className="block rounded-lg border border-gray-800 bg-gray-900 p-4 transition-colors hover:border-indigo-500"
            >
              <p className="font-medium text-white">{nav.label}</p>
              <p className="mt-1 text-sm text-gray-400">{nav.desc}</p>
            </Link>
          ))}
        </div>
      </div>
    </Layout>
  );
}
